import { useEffect, useState } from 'react';
import { House } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { TriangleDown } from '../assets/svgs/TriangleDown';
import { TriangleRight } from '../assets/svgs/TriangleRight';
import { cn } from '../lib/utils';
import type { LectureTreeNode } from '../types/Lecture';
import { mcgrawHillChapterNumber } from './mcgrawHillChapter';
import { MCGRAW_HILL_PATH } from './mcgrawHill';
import { getMcGrawHillVertical } from './mcgrawHillVerticalScope';
import { useSwitchMcGrawHillVertical } from './useSwitchMcGrawHillVertical';
import './mcgrawHillDocument.css';

interface McGrawHillChapter {
  lectureId: string;
  /** The number as written in the file name ("05"), or null. */
  number: string | null;
  name: string;
}

/** A textbook's chapters: its documents, in chapter-number order. */
export const mcgrawHillChapters = (
  nodes: readonly LectureTreeNode[],
  examId: string
): McGrawHillChapter[] =>
  nodes
    .filter((node) => node.examId === examId && node.nodeType === 'DOCUMENT')
    .map((node) => ({
      lectureId: node.id,
      number: mcgrawHillChapterNumber(node.fileName ?? ''),
      name: node.name,
      orderKey: node.orderKey,
    }))
    .sort((a, b) => {
      const byNumber =
        (a.number === null ? Infinity : Number(a.number)) -
        (b.number === null ? Infinity : Number(b.number));

      if (byNumber !== 0 && !Number.isNaN(byNumber)) return byNumber;
      return a.orderKey.localeCompare(b.orderKey);
    })
    .map(({ lectureId, number, name }) => ({ lectureId, number, name }));

/** One chapter of a book's contents. */
export interface McGrawHillContentsChapter {
  /** The document it opens: a lecture id, or a reference document's id. */
  key: string;
  /** Shown as the "Chapter N" eyebrow; null for a chapter without one. */
  number: number | null;
  title: string;
  /** Sections that jump to a page of the chapter; empty for none. */
  sections: readonly McGrawHillContentsSection[];
}

export interface McGrawHillContentsSection {
  label: string;
  /** 1-based page of the chapter's PDF. */
  page: number;
}

export interface McGrawHillChapterNavProps {
  chapters: readonly McGrawHillContentsChapter[];
  /** The chapter open in the viewer. */
  activeKey: string;
  /**
   * Swaps the viewer to another chapter (the window stays open), at `page`
   * when a section was chosen.
   */
  onSelectChapter: (key: string, page?: number) => void;
  /** Jumps the open chapter to a page. */
  onSelectPage: (page: number) => void;
  /** Closes the viewer (Home leaves it for the catalog). */
  onClose: () => void;
}

/**
 * mcgraw-hill: a textbook's table of contents (list roles on plain elements:
 * the global Sass list styles would indent an <ol>) beside an open chapter.
 * The chapters come from the open document's book (McGrawHillContents). A
 * chapter with sections is an accordion: the open one starts expanded, and a
 * section jumps to its page (opening its chapter first if needed).
 */
const McGrawHillChapterNav = ({
  chapters,
  activeKey,
  onSelectChapter,
  onSelectPage,
  onClose,
}: McGrawHillChapterNavProps) => {
  const navigate = useNavigate();
  const { selectFirstExam } = useSwitchMcGrawHillVertical();

  const [expanded, setExpanded] = useState<ReadonlySet<string>>(
    () => new Set([activeKey])
  );

  // A chapter opened from elsewhere (View Chapter, a section) shows its
  // sections.
  useEffect(() => {
    setExpanded((previous) =>
      previous.has(activeKey) ? previous : new Set([...previous, activeKey])
    );
  }, [activeKey]);

  const toggle = (key: string) =>
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  if (chapters.length === 0) return null;

  return (
    <nav
      aria-label="Contents"
      className="flex w-64 shrink-0 flex-col border-r border-border-subtle bg-surface-canvas"
    >
      <div className="mcgraw-hill-contents__header flex min-h-11 shrink-0 items-center border-b border-border-subtle px-4">
        {/* The viewer has no Collapse button here: Home closes it and goes back
            to the catalog. */}
        <Button
          variant="glass"
          size="sm"
          onClick={() => {
            // Opening a textbook made its exam active; Home hands the member
            // back their vertical's own (NCLEX-RN for Nursing, Step 1 for
            // Medicine). Started before the viewer closes and unmounts this.
            void selectFirstExam(getMcGrawHillVertical());
            onClose();
            navigate(MCGRAW_HILL_PATH);
          }}
        >
          <House aria-hidden="true" />
          Home
        </Button>
      </div>
      <div role="list" className="min-h-0 flex-1 overflow-y-auto py-2">
        {chapters.map((chapter) => {
          const isActive = chapter.key === activeKey;
          const hasSubchapters = chapter.sections.length > 0;
          const isExpanded = hasSubchapters && expanded.has(chapter.key);

          return (
            <div role="listitem" key={chapter.key}>
              <button
                type="button"
                aria-current={isActive ? 'page' : undefined}
                aria-expanded={hasSubchapters ? isExpanded : undefined}
                onClick={() => {
                  if (!isActive) {
                    onSelectChapter(chapter.key);
                    if (hasSubchapters && !isExpanded) toggle(chapter.key);
                    return;
                  }
                  if (hasSubchapters) toggle(chapter.key);
                }}
                className={cn(
                  'flex w-full items-start gap-2 px-4 py-2 text-left text-xs transition-colors',
                  isActive
                    ? 'bg-surface-raised font-medium text-fg-primary'
                    : 'text-fg-secondary hover:bg-surface-muted hover:text-fg-primary'
                )}
              >
                <span
                  className={cn(
                    'flex w-3 shrink-0 items-center',
                    // Level with the first line: the eyebrow, or the name.
                    chapter.number !== null ? 'h-4' : 'h-5'
                  )}
                  aria-hidden="true"
                >
                  {isExpanded ? <TriangleDown /> : <TriangleRight />}
                </span>
                <span className="flex min-w-0 flex-col">
                  {chapter.number !== null && (
                    <span className="text-xxs font-normal uppercase tracking-wide text-fg-secondary">
                      Chapter {chapter.number}
                    </span>
                  )}
                  <span>{chapter.title}</span>
                </span>
              </button>
              {isExpanded && (
                <div
                  role="list"
                  aria-label={`${chapter.number === null ? chapter.title : `Chapter ${chapter.number}`} sections`}
                >
                  {chapter.sections.map((section) => (
                    <div
                      role="listitem"
                      key={`${section.page}-${section.label}`}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          isActive
                            ? onSelectPage(section.page)
                            : onSelectChapter(chapter.key, section.page)
                        }
                        className="block w-full py-1.5 pl-13 pr-4 mcgraw-hill-contents__section text-left text-xs text-fg-secondary transition-colors hover:bg-surface-muted hover:text-fg-primary"
                      >
                        {section.label}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </nav>
  );
};

export default McGrawHillChapterNav;
